import { jsx as _jsx } from "react/jsx-runtime";
import { useConstant } from '@eturi/react';
import noop from 'lodash/noop';
import { createContext, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
export const I18NextContext = createContext(null);
export const I18NextProvider = ({ children, i18n, textComponent: Text, }) => {
    // NOTE: This isn't really "root" T any longer, but it's a global fixedT that
    //  has no namespace / lng. We can use this as the rootT since it acts the
    //  same and means we don't have to create separate fixedT for every useT.
    //  -
    //  This makes sure the `t` returned by `useT` always changes when the
    //  language changes, allowing it to be passed to `useMemo`.
    const getRootT = () => i18n.getFixedT(null, null);
    const [rootT, setRootT] = useState(getRootT);
    const onReadyData = useConstant(() => ({}));
    const isInitPromiseRef = useRef();
    useLayoutEffect(() => {
        if (i18n.isInitialized) {
            isInitPromiseRef.current ||= Promise.resolve();
            return;
        }
        let resolve;
        isInitPromiseRef.current ||= new Promise((r) => {
            resolve = r;
        });
        const initListener = () => {
            resolve();
        };
        const langChangeListener = () => {
            setRootT(getRootT);
        };
        i18n.on('initialized', initListener);
        i18n.on('languageChanged', langChangeListener);
        return () => {
            i18n.off('initialized', initListener);
            i18n.off('languageChanged', langChangeListener);
            isInitPromiseRef.current = undefined;
        };
    }, []);
    const createPromiseForNs = (i18n, ns) => {
        const isInitPromise = isInitPromiseRef.current;
        if (!isInitPromise)
            throw new Error('Tried to create promise without init promise');
        return Promise.all([isInitPromise, i18n.loadNamespaces(ns)]).then(noop);
    };
    const isReady = useCallback((ns) => {
        ns ??= i18n.options.defaultNS;
        if (!ns)
            throw new Error('A namespace is required');
        const dataKey = i18n.resolvedLanguage + ns;
        const data = onReadyData[dataKey];
        if (!data) {
            const isLoaded = i18n.hasLoadedNamespace(ns);
            if (!isLoaded) {
                const onLoadPromise = createPromiseForNs(i18n, ns);
                onReadyData[dataKey] = [isLoaded, onLoadPromise];
                return onLoadPromise;
            }
            onReadyData[dataKey] = [true, Promise.resolve()];
            return true;
        }
        const [isLoaded, onLoadPromise] = data;
        return isLoaded || onLoadPromise;
    }, []);
    // NOTE: We don't accept new instances of i18n or Text currently. If we need
    //  to for some reason, we can change this to a memo
    const ctx = useMemo(() => ({ i18n, isReady, rootT, Text }), [rootT]);
    return _jsx(I18NextContext.Provider, { value: ctx, children: children });
};
//# sourceMappingURL=I18NextProvider.js.map